/**
 * The dashboard frame in the Framer loader, whatever the window size:
 * 1272px wide and 40% of the height + 433px tall, its top 66px above 60% of
 * the height, tilted back 40° about its centre under a 1200px perspective.
 */
const FRAME_WIDTH = 1272;
const FRAME_HEIGHT_RATIO = 0.4;
const FRAME_HEIGHT_OFFSET = 433;
const FRAME_TOP_RATIO = 0.6;
const FRAME_TOP_OFFSET = 66;
const PERSPECTIVE = 1200;
const TILT = (40 * Math.PI) / 180;

/** Shown a bit larger than in the loader, which read too small in the app. */
const ENLARGE = 1.12;

export interface LoaderPose {
  /** Shrinks the full-window shell. */
  scale: number;
  /** Moves it down (px). */
  shift: number;
  /** The perspective (px), set before the scale shrinks it. */
  perspective: number;
}

/** How far a tilted edge `half` px from the centre recedes: its projected size factor. */
const recession = (half: number) =>
  PERSPECTIVE / (PERSPECTIVE + half * Math.sin(TILT));

/**
 * How a full-window shell, scaled about its centre, shows its tilted top
 * edge where the loader's frame shows it, then `ENLARGE`s it with that
 * edge kept in place. The shell keeps the window's proportions, so it is
 * scaled and moved to match that visible edge rather than the frame's box.
 */
export function loaderPose(width: number, height: number): LoaderPose {
  const frameHalf = (FRAME_HEIGHT_RATIO * height + FRAME_HEIGHT_OFFSET) / 2;
  const frameCentre = FRAME_TOP_RATIO * height - FRAME_TOP_OFFSET + frameHalf;
  const edgeTop =
    frameCentre - frameHalf * Math.cos(TILT) * recession(frameHalf);
  const edgeWidth = FRAME_WIDTH * recession(frameHalf);

  // the scale changes how far the shell's own top edge recedes: settle it
  let scale = FRAME_WIDTH / width;
  for (let i = 0; i < 8; i++) {
    scale = edgeWidth / (width * recession((scale * height) / 2));
  }
  scale *= ENLARGE;

  const half = (scale * height) / 2;
  const centre = edgeTop + half * Math.cos(TILT) * recession(half);
  return {
    scale,
    shift: centre - height / 2,
    perspective: PERSPECTIVE / scale,
  };
}
